
import React, { useState } from 'react';
import { MemberModel } from '../../../../models';
import { db } from '../../../../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { ChevronLeft, Send, MessageCircle } from 'lucide-react';

interface StudentCounselFormProps {
    member: MemberModel;
    onBack: () => void;
    onSave: () => void;
}

const StudentCounselForm: React.FC<StudentCounselFormProps> = ({ member, onBack, onSave }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim() || !content.trim()) return alert('제목과 내용을 입력해주세요.');

        setIsSubmitting(true);
        try {
            await addDoc(collection(db, 'studentCounsel'), {
                memberId: member.id,
                branchId: member.branchId,
                title,
                content,
                tag: '학생발신',
                date: new Date().toISOString().split('T')[0],
                counselor: member.name, // 학생이 작성자
                createdAt: Date.now(),
                isStudentWrite: true // 학생이 작성했음을 표시
            });
            onSave();
        } catch (error) {
            console.error("Error saving counsel message:", error);
            alert("메시지 전송 중 오류가 발생했습니다.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-[3rem] border border-slate-100 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
            <header className="px-8 py-6 border-b border-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="w-10 h-10 flex items-center justify-center bg-slate-50 text-slate-400 rounded-xl hover:bg-slate-100 transition-all">
                        <ChevronLeft size={20} />
                    </button>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                            <MessageCircle size={16} />
                        </div>
                        <h3 className="text-xl font-black text-slate-800 tracking-tight">선생님께 글 남기기</h3>
                    </div>
                </div>
            </header>

            <form onSubmit={handleSubmit} className="p-8 sm:p-10 space-y-8">
                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">제목</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="예: 학습 계획에 대해 궁금한 점이 있어요"
                            className="w-full bg-slate-50 border-none rounded-2xl px-6 py-4 text-sm font-bold outline-none ring-1 ring-transparent focus:ring-indigo-100 transition-all placeholder:text-slate-300"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">문의 내용</label>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="선생님께 궁금한 점이나 전하고 싶은 말을 자유롭게 적어주세요."
                            className="w-full min-h-[250px] bg-slate-50 border-none rounded-[2rem] px-6 py-6 text-sm font-medium outline-none ring-1 ring-transparent focus:ring-indigo-100 transition-all resize-none placeholder:text-slate-300 leading-relaxed"
                        />
                    </div>
                </div>

                <div className="bg-indigo-50/50 p-6 rounded-2xl border border-indigo-100/50">
                    <p className="text-[11px] font-bold text-slate-500 leading-relaxed">
                        📩 작성하신 내용은 담당 선생님께 전달되며, 선생님께서 확인 후 상담 일정을 잡거나 답변을 주실 예정입니다.
                    </p>
                </div>

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-5 bg-indigo-600 text-white rounded-[2rem] text-sm font-black shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                >
                    {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <>
                            <Send size={18} />
                            메시지 전송하기
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};

export default StudentCounselForm;
